import type { HelpContent } from '../types'

/** Help panel + first-run walkthrough copy for the Content section. Em-dash free. */
export const CONTENT_HELP: HelpContent = {
  key: 'content',
  title: 'Content',
  intro:
    'Content is where you write, schedule, and automate LinkedIn posts. AI writes a post from your topic, you see it first, then you post it now, schedule it, or turn it into an auto-post campaign. Content needs a connected LinkedIn account.',
  panel: [
    {
      heading: 'How it works',
      items: [
        { text: 'Click Generate Post, pick a topic, format, and tone, and AI writes a post.' },
        { text: 'You always see the post before anything is scheduled. Edit it if you like.' },
        { text: 'Then choose: Post now, Schedule for later, Auto-post campaign, or Save as draft.' },
      ],
    },
    {
      heading: 'Auto-post campaigns',
      items: [
        { text: 'An auto-post campaign posts in the style you reviewed on the days and time you pick, for example Mon, Wed, Fri at 9 AM.' },
        { text: 'The post you reviewed goes out first. A fresh post is written for every later slot.' },
        { text: 'Basic includes 1 automated post per week. Pro includes up to 7, split across as many campaigns as you like. The LinkedIn Poster in Settings uses the same allowance.' },
        { text: 'Pausing or deleting a campaign moves its queued posts back to drafts.' },
      ],
    },
    {
      heading: 'Post statuses',
      items: [
        { term: 'Draft', text: 'Saved, not scheduled or live. New AI posts start here.' },
        { term: 'Approved', text: 'You okayed it. Publish or schedule it next.' },
        { term: 'Scheduled', text: 'Queued to publish automatically at a set time.' },
        { term: 'Published', text: 'Live on your LinkedIn feed.' },
        { term: 'Publish Failed', text: 'A scheduled post could not go out. The card says why; publish again or reschedule.' },
      ],
    },
    {
      heading: 'The three views',
      items: [
        { term: 'Posts', text: 'All your posts as cards you can filter and act on.' },
        { term: 'Calendar', text: 'Your posts laid out by date. The dot colors match the statuses above.' },
        { term: 'Engagement', text: 'People who liked or commented on your posts. Interested ones can become leads.' },
      ],
    },
    {
      heading: 'Comment-to-DM (Pro, beta)',
      items: [
        { text: 'Turn it on in Settings, Safety. It is off by default.' },
        { text: 'Add a comment keyword to a post (for example GUIDE) and everyone who comments it gets your message by DM.' },
        { text: 'Only 1st-degree connections can be messaged, up to 10 a day, one per person per post.' },
      ],
    },
    {
      heading: 'Good to know',
      items: [
        { text: 'Publishing needs a connected LinkedIn account.' },
        { text: 'The editor flags issues, like an unfilled placeholder, that you must fix before approving.' },
      ],
    },
  ],
  tour: [
    {
      id: 'welcome',
      title: 'Your content workspace',
      body: 'Write, schedule, and automate LinkedIn posts. You see every AI post before it is scheduled.',
    },
    {
      id: 'generate',
      target: 'content-generate',
      title: 'Write a post',
      body: 'Pick a topic, format, and tone. AI writes the post, then you choose to post now, schedule it, or automate it.',
    },
    {
      id: 'auto',
      target: 'content-auto',
      title: 'Automate a style',
      body: 'Start an auto-post campaign: review a sample post, then pick the days and time posts like it go out.',
    },
    {
      id: 'stats',
      target: 'content-stats',
      title: 'Your content at a glance',
      body: 'Total posts, what published this week, drafts waiting for your review, and what is scheduled.',
    },
    {
      id: 'views',
      target: 'content-views',
      title: 'Three ways to work',
      body: 'Browse posts, see them on a calendar, or view who engaged with them.',
    },
    {
      id: 'done',
      title: 'One thing to remember',
      body: 'A generated post stays a draft until you schedule it, publish it, or start an auto-post campaign. Reopen this from Help anytime.',
    },
  ],
}
